import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaTreceM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // bg
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout paddings
      const padding = 24;
      let y = padding;

      // compute left/right widths early so title fits inside the left column
      const usableW = Math.max(0, width - padding * 2);
      const interGap = 8; // gap between columns
      const leftAreaW = Math.round(usableW * 0.65);
      const rightAreaW = Math.max(0, usableW - leftAreaW - interGap);
      const leftAreaX = padding;
      const rightAreaX = leftAreaX + leftAreaW + interGap;

      // draw left (gray) and right (white) column backgrounds before the title
      ctx.fillStyle = opts.leftAreaBg || '#ffffff';
      ctx.fillRect(leftAreaX, 0, leftAreaW, height);

      ctx.fillStyle = opts.rightAreaBg || '#ffffff';
      ctx.fillRect(rightAreaX, 0, rightAreaW, height);

      // title square + title (placed inside the left column with a small inner inset)
      const titleFontSize = opts.titleFontSize || 36;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 0.9);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 14;
      const squareText = opts.titleSquareText || '2.1';
      const innerLeftPad = 8;
      const squareX = leftAreaX + innerLeftPad;

      // square (inside left column)
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      // title (wrapped/justified) constrained to left column
      const title = opts.title || props.title || '';
      const titleX = squareX + squareSize + squareGap;
      const titleMaxW = Math.min(
        typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : 1200,
        Math.max(0, leftAreaW - (squareSize + squareGap) - innerLeftPad - 12)
      );

      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const justifyAndWrap = (c, text, x, y0, maxW, lineH) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });

        let y = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            // justify
            const gaps = ln.words.length - 1;
            const extra = (maxW - ln.wordsWidth) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              c.fillText(ln.words[j], cx, y);
              cx += c.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, y);
          }
          y += lineH;
        }
        return y;
      };

      const titleLineH = Math.round(titleFontSize * 1.2);
      const afterTitleY = justifyAndWrap(ctx, title, titleX, y, titleMaxW, titleLineH);

      // subtitle under title
      const subTitle = opts.subTitle || props.subTitle || '';
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 22;
        ctx.font = `${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLineH = Math.round(subFontSize * 1.2);
        const afterSubY = justifyAndWrap(ctx, subTitle, titleX, afterTitleY + 8, titleMaxW, subLineH);
        y = Math.max(y + squareSize, afterSubY) + 20;
      } else {
        y = Math.max(y + squareSize, afterTitleY) + 20;
      }

      // data
      const graficaUnoData = props.graficaUnoData || [];

      // charts start
      const chartsTopGap = typeof opts.chartsTopGap === 'number' ? opts.chartsTopGap : 28;
      const chartsStartY = y + chartsTopGap;

      const leftPad = 12;
      const areaX = leftAreaX + leftPad;
      const areaW = Math.max(0, leftAreaW - leftPad * 2);
      const areaTop = chartsStartY;
      const areaBottom = Math.max(areaTop + 120, height - padding - 140);

      // normalize items
      const data = graficaUnoData.map(it => ({
        comuna: it.comuna || '',
        hogaresMesAnterior: Number(it.hogaresMesAnterior) || 0,
        hogaresUltimoMes: Number(it.hogaresUltimoMes) || 0
      }));
      // sort by total descending
      data.sort((a, b) => (b.hogaresUltimoMes + b.hogaresMesAnterior) - (a.hogaresUltimoMes + a.hogaresMesAnterior));

      const maxVal = Math.max(1, ...data.flatMap(i => [i.hogaresMesAnterior, i.hogaresUltimoMes]));

      // legend on top of area
      ctx.font = `${opts.legendFontSize || 20}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const legendY = areaTop;
      const boxW = 12;
      const textGap = 6;
      const itemGap = 24;
      const tActual = 'Último mes';
      const tAnterior = 'Mes anterior';
      const wActual = ctx.measureText(tActual).width;
      const wAnterior = ctx.measureText(tAnterior).width;
      const legendW = boxW + textGap + wActual + itemGap + boxW + textGap + wAnterior;
      const legendX = areaX + Math.max(0, Math.round((areaW - legendW) / 2));

      ctx.fillStyle = opts.colorAct || '#06b6d4';
      ctx.fillRect(legendX, legendY, boxW, boxW);
      ctx.fillStyle = opts.legendColor || '#374151';
      ctx.fillText(tActual, legendX + boxW + textGap, legendY - 2);
      const secondX = legendX + boxW + textGap + wActual + itemGap;
      ctx.fillStyle = opts.colorAnt || '#9ca3af';
      ctx.fillRect(secondX, legendY, boxW, boxW);
      ctx.fillStyle = opts.legendColor || '#374151';
      ctx.fillText(tAnterior, secondX + boxW + textGap, legendY - 2);

      // Vertical grouped bars layout: two bars per comuna (Anterior gray, Actual blue)
      const n = data.length;
      const barsTop = areaTop + 24;
      const availableW = Math.max(40, areaW);
      const groupGap = Math.max(2, Math.min(8, Math.floor(availableW / Math.max(1, n * 10))));
      const pairGap = 2;
      const groupW = Math.max(8, Math.floor((availableW - Math.max(0, (n - 1) * groupGap)) / Math.max(1, n)));
      const singleBarW = Math.max(3, Math.floor((groupW - pairGap) / 2));
      const usedW = n * (singleBarW * 2 + pairGap) + Math.max(0, (n - 1) * groupGap);
      const barsLeft = areaX + Math.max(0, Math.round((areaW - usedW) / 2));

      const totalChartH = Math.max(60, areaBottom - barsTop);
      const barMaxH = Math.max(40, Math.round(totalChartH * 0.8));
      const labelSpace = Math.max(40, totalChartH - barMaxH);
      const itemValueFontSize = opts.itemValueFontSize || 15;

      for (let i = 0; i < n; i++) {
        const it = data[i];
        const groupX = barsLeft + i * ((singleBarW * 2 + pairGap) + groupGap);
        const antX = groupX;
        const actX = groupX + singleBarW + pairGap;

        const antH = Math.round((it.hogaresMesAnterior / maxVal) * barMaxH);
        const actH = Math.round((it.hogaresUltimoMes / maxVal) * barMaxH);

        // bar backgrounds
        ctx.fillStyle = opts.barBg || '#e6e7ea';
        ctx.fillRect(antX, barsTop, singleBarW, barMaxH);
        ctx.fillRect(actX, barsTop, singleBarW, barMaxH);

        // anterior (gray)
        if (antH > 0) {
          ctx.fillStyle = opts.colorAnt || '#9ca3af';
          ctx.fillRect(antX, barsTop + (barMaxH - antH), singleBarW, antH);
          if (antH >= itemValueFontSize + 4 && singleBarW > 10) {
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `${Math.max(8, itemValueFontSize - 2)}px Inter, Arial, sans-serif`;
            ctx.fillText(String(it.hogaresMesAnterior || 0), antX + Math.round(singleBarW / 2), barsTop + (barMaxH - antH) + Math.round(antH / 2));
          }
        }

        // actual (blue)
        if (actH > 0) {
          ctx.fillStyle = opts.colorAct || '#06b6d4';
          ctx.fillRect(actX, barsTop + (barMaxH - actH), singleBarW, actH);
          if (actH >= itemValueFontSize + 4 && singleBarW > 10) {
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `${Math.max(8, itemValueFontSize - 2)}px Inter, Arial, sans-serif`;
            ctx.fillText(String(it.hogaresUltimoMes || 0), actX + Math.round(singleBarW / 2), barsTop + (barMaxH - actH) + Math.round(actH / 2));
          }
        }

        // comuna label diagonal under pair (full text, no truncation)
        const labelFontSize = Math.max(7, Math.min(15, Math.floor(groupW / 2)));
        const rawLabel = String(it.comuna || '');
        const labelX = groupX + Math.round((singleBarW * 2 + pairGap) / 2) - 25;
        const labelY = barsTop + barMaxH + Math.max(8, Math.round(labelSpace * 0.15)) + 120;
        ctx.save();
        ctx.translate(labelX, labelY);
        ctx.rotate(-Math.PI / 2.5);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.font = `bold ${labelFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.itemLabelColor || '#0f172a';
        ctx.fillText(rawLabel, 0, 0);
        ctx.restore();
      }

      // set y to below this left chart area
      y = barsTop + barMaxH + labelSpace;

      // bottom texts: textoUno
      const textoUno = opts.textoUno || props.textoUno || '';
      ctx.font = `${opts.bottomFontSize || 20}px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.bottomColor || '#374151';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const drawSmallWrap = (c, text, x, y0, maxW, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxW && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      const leftTextX = leftAreaX + 12;
      const leftTextMaxW = leftAreaW - 24;
      const leftTextY = y + 10;
      if (textoUno) drawSmallWrap(ctx, textoUno, leftTextX, leftTextY, leftTextMaxW, 18);

      // RIGHT SIDE: accept array or string for textoRight and render paragraphs centered vertically
      const textoRightRaw = opts.textoRight ?? opts.rightText ?? props.textoRight ?? props.rightText ?? '';
      const rightParagraphs = [];
      if (Array.isArray(textoRightRaw)) {
        for (let p of textoRightRaw) if (String(p || '').trim()) rightParagraphs.push(String(p));
      } else if (String(textoRightRaw || '').trim()) {
        rightParagraphs.push(String(textoRightRaw));
      }

      if (rightParagraphs.length) {
        ctx.save();

        const wrapLinesWithMeta = (c, text, maxW) => {
          const words = String(text).split(/\s+/).filter(Boolean);
          const lines = [];
          let cur = [];
          let curW = 0;
          const spaceW = c.measureText(' ').width;
          for (let w of words) {
            const ww = c.measureText(w).width;
            if (cur.length === 0) {
              cur.push(w); curW = ww;
            } else if (curW + spaceW + ww <= maxW) {
              cur.push(w); curW += spaceW + ww;
            } else {
              lines.push({ words: cur.slice(), wordsWidth: curW });
              cur = [w]; curW = ww;
            }
          }
          if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });
          return lines;
        };

        let rightFontSize = typeof opts.textoRightSize === 'number' ? opts.textoRightSize : 25;
        const minFontSize = 8;
        ctx.fillStyle = opts.textoRightColor || '#374151';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        const maxRightW = Math.max(8, rightAreaW - 24);
        const topBound = padding;
        const bottomBound = height - padding;

        let paragraphLines = [];
        let lineH = 0;
        let totalH = 0;
        while (rightFontSize >= minFontSize) {
          ctx.font = `${rightFontSize}px Inter, Arial, sans-serif`;
          paragraphLines = [];
          for (let p of rightParagraphs) {
            const wrapped = wrapLinesWithMeta(ctx, p, maxRightW);
            if (wrapped.length === 0) wrapped.push({ words: [''], wordsWidth: 0 });
            paragraphLines.push(wrapped);
          }
          const totalLines = paragraphLines.reduce((sum, para, idx) => sum + para.length + (idx < paragraphLines.length - 1 ? 1 : 0), 0);
          lineH = Math.round(rightFontSize * 1.3);
          totalH = totalLines * lineH;
          if (totalH <= (bottomBound - topBound) || rightFontSize === minFontSize) break;
          rightFontSize -= 1;
        }

        const spaceW = ctx.measureText(' ').width;
        const startY = topBound + Math.round(((bottomBound - topBound) - totalH) / 2) + Math.round(lineH / 2);
        const leftX = rightAreaX + 12;

        ctx.fillStyle = opts.textoRightColor || '#374151';
        let currentY = startY;
        for (let pIdx = 0; pIdx < paragraphLines.length; pIdx++) {
          const para = paragraphLines[pIdx];
          for (let lIdx = 0; lIdx < para.length; lIdx++) {
            const ln = para[lIdx];
            const isLastLineOfParagraph = lIdx === para.length - 1;
            if (!isLastLineOfParagraph && ln.words.length > 1) {
              // justify
              const gaps = ln.words.length - 1;
              const extra = (maxRightW - ln.wordsWidth) / gaps;
              let cx = leftX;
              for (let j = 0; j < ln.words.length; j++) {
                ctx.fillText(ln.words[j], cx, currentY);
                cx += ctx.measureText(ln.words[j]).width + spaceW + extra;
              }
            } else {
              ctx.fillText(ln.words.join(' '), leftX, currentY);
            }
            currentY += lineH;
          }
          if (pIdx < paragraphLines.length - 1) currentY += lineH; // blank line between paragraphs
        }

        ctx.restore();
      }

      // footer text
      const footerText = opts.footerText || props.footerText || '';
      ctx.fillStyle = opts.footerColor || '#6b7280';
      ctx.font = `${opts.footerFontSize ?? 18}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';
      const footerInset = typeof opts.footerInset === 'number' ? opts.footerInset : 8;
      const footerMaxW = Math.max(0, rightAreaW - footerInset - 8);

      const wrapFooter = (c, text, maxW) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = '';
        for (let w of words) {
          const test = cur ? cur + ' ' + w : w;
          if (c.measureText(test).width <= maxW) {
            cur = test;
          } else {
            if (cur) lines.push(cur);
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      const footerLines = wrapFooter(ctx, footerText, footerMaxW);
      const footerLineH = Math.round((opts.footerLineHeight ?? 1.2) * (opts.footerFontSize ?? 18));
      const footerBlockH = footerLines.length * footerLineH;
      const defaultFooterY = height - padding - footerBlockH - 30;
      let footerY = defaultFooterY;
      footerY = Math.max(padding, Math.min(footerY, height - padding - footerBlockH));
      const footerX = rightAreaX + rightAreaW - footerInset;
      for (let i = 0; i < footerLines.length; i++) {
        ctx.fillText(footerLines[i], footerX, footerY + i * footerLineH);
      }

      // optional logo bottom-right
      const mainLogoSrc = opts.logoSrc || props.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_trece_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});